import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/utils/api";
import Hls from "hls.js";

export default function VideoPlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const progressInterval = useRef(null);

  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [courseId]);

  useEffect(() => {
    if (course && progress) {
      initializeVideo();
    }
  }, [currentModuleIndex, course, progress]);

  const fetchData = async () => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get("/progress/my-courses"),
      ]);

      setCourse(courseRes.data);
      const prog = progressRes.data.find((p) => p.course_id === courseId);
      setProgress(prog);

      // Find first incomplete module
      if (prog && prog.module_progress.length > 0) {
        const firstIncomplete = prog.module_progress.findIndex(
          (m) => !m.quiz_passed
        );
        if (firstIncomplete !== -1) {
          setCurrentModuleIndex(firstIncomplete);
        }
      }
    } catch (error) {
      toast.error("Failed to load course");
    } finally {
      setLoading(false);
    }
  };

  const initializeVideo = () => {
    if (!videoRef.current || !course) return;

    const module = course.modules[currentModuleIndex];
    const videoUrl = module.video_url;

    // Clean up previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
    }

    // Check if HLS is supported
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(videoUrl);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // Resume from last position
        const lastPos =
          progress?.module_progress[currentModuleIndex]?.last_position || 0;
        if (lastPos > 0) {
          videoRef.current.currentTime = lastPos;
        }
      });

      hlsRef.current = hls;
    } else if (
      videoRef.current.canPlayType("application/vnd.apple.mpegurl")
    ) {
      // Native HLS support (Safari)
      videoRef.current.src = videoUrl;
      videoRef.current.addEventListener("loadedmetadata", () => {
        const lastPos =
          progress?.module_progress[currentModuleIndex]?.last_position || 0;
        if (lastPos > 0) {
          videoRef.current.currentTime = lastPos;
        }
      });
    }

    // Start progress tracking
    startProgressTracking();
  };

  const startProgressTracking = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    progressInterval.current = setInterval(() => {
      if (videoRef.current && course) {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration || course.modules[currentModuleIndex].video_duration;
        const progressPercent = (currentTime / duration) * 100;

        // Save progress every 10 seconds
        saveProgress(progressPercent, currentTime);
      }
    }, 10000);
  };

  const saveProgress = async (videoProgress, lastPosition) => {
    try {
      await api.post("/progress/update", {
        course_id: courseId,
        module_index: currentModuleIndex,
        video_progress: videoProgress,
        last_position: lastPosition,
      });
      // Show occasional progress saved notification
      if (Math.floor(lastPosition) % 30 === 0 && lastPosition > 0) {
        toast.success("Progress saved!", { duration: 2000 });
      }
    } catch (error) {
      console.error("Failed to save progress", error);
    }
  };

  const handleVideoEnded = () => {
    // Video completed, prompt to take quiz
    toast.success("Video completed! Take the quiz to continue.");
  };

  const goToQuiz = () => {
    navigate(`/quiz/${courseId}/${currentModuleIndex}`);
  };

  const handleModuleSelect = (index) => {
    // Check if previous modules are completed
    if (index > 0 && progress) {
      const prevModule = progress.module_progress[index - 1];
      if (!prevModule || !prevModule.quiz_passed) {
        toast.error("Complete previous modules first");
        return;
      }
    }
    setCurrentModuleIndex(index);
  };

  if (loading) {
    return (
      <div className="worker-ui min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2e7fd4] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading video...</p>
        </div>
      </div>
    );
  }

  if (!course) return null;

  const currentModule = course.modules[currentModuleIndex];
  const moduleProgress =
    progress?.module_progress[currentModuleIndex] || {};
  const isModuleCompleted = moduleProgress.quiz_passed;

  return (
    <div className="worker-ui min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#0a2240] text-white px-4 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate("/worker/dashboard")}
          className="flex items-center text-white mb-2"
        >
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-xl font-bold">{course.title}</h1>
        <p className="text-sm text-slate-300 mt-1">
          Module {currentModuleIndex + 1} of {course.modules.length}
        </p>
      </div>

      {/* Video Player */}
      <div className="relative bg-black">
        <video
          ref={videoRef}
          controls
          onEnded={handleVideoEnded}
          className="w-full"
          style={{ maxHeight: "50vh" }}
          data-testid="video-player"
        >
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Module Info */}
      <div className="px-4 py-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          {currentModule.title}
        </h2>
        <p className="text-slate-600 mb-6">{currentModule.description}</p>

        {isModuleCompleted ? (
          <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-green-600 mr-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-bold text-green-900">Module Completed</p>
                <p className="text-sm text-green-700">
                  Score: {moduleProgress.quiz_score?.toFixed(0)}%
                </p>
              </div>
            </div>
          </div>
        ) : (
          <Button
            onClick={goToQuiz}
            className="w-full thumb-button bg-[#2e7fd4] hover:bg-[#1c64b0] text-white"
            data-testid="take-quiz-button"
          >
            TAKE QUIZ
          </Button>
        )}

        {/* Module List */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">All Modules</h3>
          <div className="space-y-2">
            {course.modules.map((module, index) => {
              const modProg = progress?.module_progress[index] || {};
              const isCompleted = modProg.quiz_passed;
              const isLocked = index > 0 && !progress?.module_progress[index - 1]?.quiz_passed;

              return (
                <button
                  key={index}
                  onClick={() => handleModuleSelect(index)}
                  disabled={isLocked}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    index === currentModuleIndex
                      ? "border-[#2e7fd4] bg-blue-50"
                      : isCompleted
                      ? "border-green-500 bg-green-50"
                      : isLocked
                      ? "border-slate-200 bg-slate-50 opacity-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {index + 1}. {module.title}
                      </p>
                    </div>
                    {isCompleted ? (
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : isLocked ? (
                      <svg className="w-6 h-6 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="h-24"></div>
    </div>
  );
}
